/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Presentational building blocks for the pre-launch mobile nav: a single nav button
 * and the overflow ("More") menu popover.
 */

import React from'react';
import { Box, UnstyledButton, Text, Stack } from'@/shared/ui';
import { cn } from'@/shared/ui/utils/tailwind';
import type { NavItem } from'./MobileNav.types';

const indicator = (
 <Text component="span" className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-(--seed-base)"/>
);

const labelClass ='text-meta tracking-(--tracking-wide) font-(--font-light) whitespace-nowrap leading-none';

interface NavButtonProps {
  item: NavItem;
  isActive: boolean;
}

export const NavButton: React.FC<NavButtonProps> = ({ item, isActive }) => {
  const Icon = item.icon;
  return (
    <UnstyledButton
      onClick={item.action}
      className={cn(
        'flex-1 flex flex-col items-center justify-center gap-(--space-1) relative',
        'text-(--text-tertiary) transition-colors duration-200',
        isActive &&'text-(--seed-base)',
        isActive &&'active',
      )}
    >
      {isActive && indicator}
      <Icon className="w-5 h-5" />
      <Text component="span" className={labelClass}>{item.label}</Text>
    </UnstyledButton>
  );
};

interface OverflowMenuProps {
  items: NavItem[];
  activeSection?: string;
  menuTitle: string;
  onSelect: (item: NavItem) => void;
}

export const OverflowMenu: React.FC<OverflowMenuProps> = ({ items, activeSection, menuTitle, onSelect }) => (
  <Box
    className={cn(
      'absolute bottom-[calc(100%+12px)] right-0',
      'w-[min(320px,90vw)]',
      'p-(--space-4)',
      'bg-(--surface-primary)',
      'border border-(--border-subtle)',
      'shadow-lg z-1001',
    )}
  >
    <Text className="text-small font-(--font-semibold) tracking-(--tracking-wider) uppercase text-(--text-tertiary) mb-(--space-3)">
      {menuTitle}
    </Text>
    <Stack gap={4}>
      {items.map((item) => {
        const isItemActive = activeSection === item.id;
        return (
          <UnstyledButton
            key={item.id}
            onClick={() => onSelect(item)}
            className={cn(
              'w-full text-left p-(--space-3)',
              'border border-(--border-subtle)',
              'bg-(--surface-secondary)',
              'transition-[background-color,border-color] duration-150',
              'hover:bg-(--surface-hover) hover:border-(--border-medium)',
              isItemActive &&'border-(--seed-base) bg-(--selection-active)',
            )}
          >
            <Text className="text-body font-(--font-semibold) text-(--text-primary)">{item.label}</Text>
            {item.description && (
              <Text className="text-small text-(--text-secondary) mt-(--space-1)">{item.description}</Text>
            )}
          </UnstyledButton>
        );
      })}
    </Stack>
  </Box>
);
